import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../layouts/App/hooks/useAppSelector';
import { logout } from '../../../../store/slices/authSlice';
import { signOutAndClearStorage } from '../../../../services/auth/signOut';
import {
  describeNotification,
  muteKey,
  muteTargetFor,
  notificationHref,
  useNotifications,
} from '../../../../services/notifications/useNotifications';
import './SideNav.css';

export type V2NavSection = 'feed' | 'trips' | 'explore' | 'messages' | 'calendar' | 'settings' | 'ask-ai';

interface NavItem {
  id: V2NavSection;
  label: string;
  path: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'feed',
    label: 'Feed',
    path: '/feed',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: 'trips',
    label: 'Trips',
    path: '/trips',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    id: 'calendar',
    label: 'Calendar',
    path: '/calendar',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'explore',
    label: 'Explore',
    path: '/explore',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs>
    <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop stop-color="#0d4f3c"/>
      <stop offset="1" stop-color="#d9ff31"/>
    </linearGradient>
  </defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>
`)}`;

const latestMessages = [
  { name: 'Maya Chen', text: 'Are you still free for the Lisbon walk?', unread: true },
  { name: 'Leo Garcia', text: 'Shared a new Rome food list with you.', unread: true },
  { name: 'Emma Wilson', text: 'Santorini tips are ready.', unread: false },
  { name: 'Nora Smith', text: 'Can we move the museum stop?', unread: false },
  { name: 'Trip Concierge', text: 'Your weekend plan has 3 new ideas.', unread: true },
];

interface SideNavProps {
  activeSection: V2NavSection;
}

const SideNav: React.FC<SideNavProps> = ({ activeSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { userProfile, restUser } = useAppSelector((state) => state.auth);
  const avatar = restUser?.picture_url || restUser?.avatarUrl || userProfile?.avatar || fallbackAvatar;
  const {
    notifications,
    unread,
    isLoading: isLoadingNotifications,
    error: notificationsError,
    muted,
    muteEntity,
    refreshInbox,
    markAllAsRead,
  } = useNotifications();

  const isActive = (item: NavItem) => {
    if (item.id === activeSection) return true;
    if (item.id === 'feed') {
      return location.pathname === '/feed' || location.pathname === '/';
    }
    return location.pathname.startsWith(item.path);
  };

  // Profiles live at /user/:id now; the avatar is "active" only on YOUR OWN
  // profile, not when viewing someone else's. (/profile still redirects here.)
  const ownProfilePath = restUser?.id ? `/user/${restUser.id}` : '/profile';
  const isProfileActive =
    location.pathname.startsWith('/profile') ||
    (Boolean(restUser?.id) && location.pathname === `/user/${restUser?.id}`);
  const isSettingsActive = location.pathname === '/settings';

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const renderNavItems = (onNavigate: (path: string) => void) => (
    <ul className="v2-side-nav__list">
      {NAV_ITEMS.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className={`v2-side-nav__item ${isActive(item) ? 'v2-side-nav__item--active' : ''}`}
            onClick={() => onNavigate(item.path)}
            aria-current={isActive(item) ? 'page' : undefined}
            aria-label={item.label}
            title={item.label}
          >
            {item.icon}
            <span className="v2-side-nav__label">{item.label}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <nav className="v2-side-nav" aria-label="Main navigation">
      <div className="v2-side-nav__leading">
        <button type="button" className="v2-side-nav__brand" onClick={() => handleNavigate('/feed')}>
          <span className="v2-side-nav__mark">W</span>
          <span className="v2-side-nav__brand-name">Wayplume</span>
        </button>

        <div className="v2-side-nav__nav-wrap v2-side-nav__nav-wrap--inline">
          {renderNavItems(handleNavigate)}
        </div>
      </div>

      <div className="v2-side-nav__nav-wrap v2-side-nav__nav-wrap--desktop">
        {renderNavItems(handleNavigate)}
      </div>

      <div className="v2-side-nav__actions">
        <label className="v2-side-nav__search">
          <span className="v2-side-nav__search-icon" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <input type="search" placeholder="Search" aria-label="Search" />
        </label>
        <button
          type="button"
          className={`v2-side-nav__ask ${activeSection === 'ask-ai' || location.pathname === '/ask-ai' ? 'v2-side-nav__ask--active' : ''}`}
          onClick={() => navigate('/ask-ai')}
        >
          Plan with AI
        </button>
        {/* The list loads on OPEN, not on mount: the dropdown is hover/focus
            driven in CSS, so these handlers are the only signal that the user
            is actually looking at it. The badge polls separately and cheaply. */}
        <div
          className="v2-side-nav__action-menu"
          onMouseEnter={() => { void refreshInbox(); }}
          onFocus={() => { void refreshInbox(); }}
        >
          <button
            type="button"
            className="v2-side-nav__icon-button"
            aria-label={unread > 0 ? `Show notifications, ${unread} unread` : 'Show notifications'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {unread > 0 && (
              <span className="v2-side-nav__badge">{unread > 9 ? '9+' : unread}</span>
            )}
          </button>
          <div className="v2-side-nav__dropdown">
            <div className="v2-side-nav__dropdown-head">
              <h3>Notifications</h3>
              {unread > 0 && (
                <button
                  type="button"
                  className="v2-side-nav__mark-read"
                  onClick={() => { void markAllAsRead(); }}
                >
                  Mark all read
                </button>
              )}
            </div>
            {notificationsError ? (
              <p>{notificationsError}</p>
            ) : notifications.length === 0 ? (
              <p>{isLoadingNotifications ? 'Loading…' : 'Nothing new right now.'}</p>
            ) : (
              notifications.map((item) => {
                // A row is a DIV wrapping two buttons, not one button: nesting
                // the mute control inside the navigate control would be invalid
                // HTML and would fire both on click.
                const target = muteTargetFor(item);
                const isMuted = target ? muted.has(muteKey(target)) : false;
                return (
                  <div
                    key={item.notificationId}
                    className={`v2-side-nav__notification ${item.readAt ? '' : 'v2-side-nav__notification--unread'}`}
                  >
                    <button
                      type="button"
                      className="v2-side-nav__notification-open"
                      onClick={() => navigate(notificationHref(item))}
                    >
                      <span>{describeNotification(item)}</span>
                      {item.preview && <em>{item.preview}</em>}
                    </button>
                    {target && (
                      <button
                        type="button"
                        className="v2-side-nav__notification-mute"
                        disabled={isMuted}
                        title={isMuted ? 'Muted — turn back on in Settings' : target.label}
                        aria-label={isMuted ? 'Muted' : target.label}
                        onClick={() => { void muteEntity(target); }}
                      >
                        {isMuted ? 'Muted' : 'Mute'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="v2-side-nav__action-menu">
          <button
            type="button"
            className={`v2-side-nav__icon-button ${activeSection === 'messages' ? 'v2-side-nav__icon-button--active' : ''}`}
            onClick={() => navigate('/messages')}
            aria-label="Open messages"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="v2-side-nav__dot" />
          </button>
          <div className="v2-side-nav__dropdown v2-side-nav__dropdown--messages">
            <h3>Messages</h3>
            {latestMessages.map((message) => (
              <div key={message.name} className="v2-side-nav__message-preview">
                <span className={message.unread ? 'v2-side-nav__message-dot' : ''} />
                <div>
                  <strong>{message.name}</strong>
                  <p>{message.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="v2-side-nav__action-menu">
          <button
            type="button"
            className={`v2-side-nav__avatar ${isProfileActive || isSettingsActive ? 'v2-side-nav__avatar--active' : ''}`}
            aria-label="Go to profile"
            onClick={() => navigate(ownProfilePath)}
          >
            <img
              src={avatar}
              alt=""
              onError={(event) => {
                event.currentTarget.src = fallbackAvatar;
              }}
            />
          </button>
          <div className="v2-side-nav__dropdown v2-side-nav__dropdown--user">
            <button type="button" onClick={() => navigate('/settings')}>Settings</button>
            <button
              type="button"
              onClick={async () => {
                // Tokens off the disk BEFORE the app forgets the user — resetting
                // Redux alone left them there, and the next load signed back in.
                await signOutAndClearStorage();
                dispatch(logout());
                navigate('/', { replace: true });
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default SideNav;
